import { apiFehlerText } from '@/lib/fehlertext';
import { KUNDE_STATUSES, LEAD_STATUSES, type CustomerStatus } from '@/types';

/**
 * Lead und Kunde sind derselbe Datensatz; welche Phase er hat, sagt allein sein
 * Status (core: `LEAD_STATUSES` / `KUNDE_STATUSES`). Deshalb wird die Phase hier
 * aus dem Datensatz abgeleitet und nicht von der Seite behauptet, die ihn zeigt:
 * Die Leadliste zeigte bis zum 29.10.2026 gar keine Statusauswahl, die
 * Kundenliste alle sieben Kundenstatus. Haenge man in der Leadliste dieselben
 * sieben Status auf, waere aus einem Lead per Statuswechsel ein Kunde geworden —
 * ohne K-Nummer, ohne Aktivierungsdatum, ohne Anfrage.
 *
 * Genau das tat die unbeworbene Bearbeiten-Seite `/customers/<lead-id>/edit`,
 * bis !386 sie am 29.10.2026 verengt hat: Sie leitet die erlaubten Status jetzt
 * selbst aus dem geladenen Satz ab (`istLead`/`erlaubteStatus` in
 * `CustomerFormPage.tsx`). Das ist dieselbe Regel, aber eine zweite Kopie
 * davon — die Phasenregel steht damit an zwei Stellen in der Oberflaeche und
 * an keiner am Server. Offener Punkt: Sie gehoert an den Server, der den
 * Statuswechsel annimmt, dann koennen die Auswahllisten sie nur noch anzeigen
 * und nicht mehr einzeln vergessen. Solange das nicht so ist, muss jede neue
 * Auswahlliste die Ableitung von hier nehmen und sie nicht nachbauen.
 *
 * Dieselbe Regel gilt in der Gegenrichtung: `/leads/<kunde-id>` laedt heute
 * jeden Datensatz (`customersApi.get`, keine Phasenpruefung). Haette die
 * Statusauswahl dort der Seite geglaubt statt dem Datensatz, haette sie einem
 * echten Kunden die Lead-Status angeboten und ihn mit einem Klick in die
 * Lead-Phase geholt — K-Nummer und `activated_at` blieben stehen.
 */
export type StatusPhase = 'lead' | 'kunde';

export function statusPhase(status: CustomerStatus): StatusPhase {
  return LEAD_STATUSES.includes(status) ? 'lead' : 'kunde';
}

/**
 * Die Status, die in dieser Phase per Statuswechsel erreichbar sind — in der
 * Reihenfolge des Arbeitsablaufs. Der Weg von Lead zu Kunde steht bewusst nicht
 * darin: er laeuft ueber `POST /leads/{id}/convert`, der die K-Nummer zieht und
 * `activated_at` setzt. Ein Statuswechsel umgeht das.
 */
export function statusAuswahl(phase: StatusPhase): readonly CustomerStatus[] {
  return phase === 'lead' ? LEAD_STATUSES : KUNDE_STATUSES;
}

/**
 * Meldung in Deutsch, wenn ein Statuswechsel scheitert. Die Zweige stehen in
 * `@/lib/fehlertext`, weil sie nicht statusspezifisch sind; hier steht nur,
 * worum es ging.
 */
export function statusFehlerText(fehler: unknown): string {
  return apiFehlerText(fehler, 'Der Statuswechsel');
}
